import { useCallback, useEffect, useRef, useState } from "react";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { supabase } from "@/integrations/supabase/client";

/**
 * useVoiceInput (TES-1172, WP6)
 *
 * Manages the voice recording state machine for the AI chat:
 *   idle → recording → transcribing → idle (or error)
 *
 * Records via MediaRecorder, then POSTs the audio (base64 over JSON — mirroring
 * the existing `agent-request` transport in src/lib/agent-request.ts) to the
 * user-JWT-scoped `transcribe-audio` Edge Function, which calls OpenAI's
 * `gpt-4o-transcribe` model. The resulting transcript is handed back via
 * `onTranscript` and is NOT auto-sent — it lands in the chat input.
 *
 * Errors are surfaced as stable codes (see `VoiceErrorCode`) so the UI can map
 * them to localized messages. The OPENAI_API_KEY lives only server-side; this
 * hook never sees a provider key.
 */

/** MediaRecorder MIME types in priority order. */
const AUDIO_TYPES = ["audio/webm", "audio/ogg", "audio/mp4"];

/** Stable backend code when OPENAI_API_KEY is not configured. */
const TRANSCRIBE_NOT_CONFIGURED_CODE = "TRANSCRIBE_NOT_CONFIGURED";

/** Stable error codes surfaced to the UI for i18n mapping. */
export type VoiceErrorCode = "micDenied" | "noProvider" | "transcriptionFailed";

export interface UseVoiceInputOptions {
  /** Called with the transcribed text after a successful recording. */
  onTranscript: (text: string) => void;
  /** BCP 47 language tag forwarded to the model (default: 'de'). */
  language?: string;
}

export interface UseVoiceInputResult {
  isRecording: boolean;
  isTranscribing: boolean;
  /** True when the browser supports MediaRecorder + getUserMedia. */
  isSupported: boolean;
  /** Stable error code set on failure; null otherwise. */
  error: VoiceErrorCode | null;
  /** Start microphone recording — no-op if offline, unsupported or already recording. */
  startRecording: () => Promise<void>;
  /** Stop recording and trigger transcription. */
  stopRecording: () => void;
}

/** Convert a Blob to a raw base64 string (no data: prefix). */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(",")[1] ?? "");
    };
    reader.onerror = () => reject(new Error("FileReader failed"));
    reader.readAsDataURL(blob);
  });
}

export function useVoiceInput({
  onTranscript,
  language = "de",
}: UseVoiceInputOptions): UseVoiceInputResult {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState<VoiceErrorCode | null>(null);

  const { isOnline } = useOnlineStatus();

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const mimeTypeRef = useRef<string>("");

  const isSupported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined";

  // Cleanup on unmount: abort in-flight fetch, stop recorder + tracks.
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const transcribe = useCallback(
    async (blob: Blob) => {
      setIsTranscribing(true);
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token) {
          setError("transcriptionFailed");
          setIsTranscribing(false);
          return;
        }

        const base64 = await blobToBase64(blob);
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/transcribe-audio`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              "Content-Type": "application/json",
              apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            },
            body: JSON.stringify({
              audio: base64,
              mimeType: mimeTypeRef.current || "audio/webm",
              language,
            }),
            signal: abortController.signal,
          },
        );

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          const code = (body as { code?: string }).code;
          setError(
            code === TRANSCRIBE_NOT_CONFIGURED_CODE || response.status === 503
              ? "noProvider"
              : "transcriptionFailed",
          );
          setIsTranscribing(false);
          return;
        }

        const data = (await response.json().catch(() => ({}))) as {
          text?: string;
        };
        const text = data.text?.trim();
        if (text) {
          onTranscript(text);
        } else {
          // Empty transcript (silence / unrecognizable audio): surface feedback
          // instead of a silent no-op so the user knows nothing was captured.
          setError("transcriptionFailed");
        }
        setIsTranscribing(false);
      } catch (err) {
        if ((err as Error)?.name === "AbortError") {
          setIsTranscribing(false);
          return;
        }
        setError("transcriptionFailed");
        setIsTranscribing(false);
      }
    },
    [language, onTranscript],
  );

  const startRecording = useCallback(async () => {
    // Guard against offline, no browser support, an in-progress recording, AND
    // an in-flight transcription — starting again mid-transcription would
    // overwrite abortControllerRef without aborting the previous request.
    if (!isOnline || isRecording || isTranscribing || !isSupported) return;

    setError(null);

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      // Permission denied / no device.
      setError("micDenied");
      return;
    }

    streamRef.current = stream;

    const mimeType = AUDIO_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) ?? "";
    mimeTypeRef.current = mimeType;

    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
    recorderRef.current = recorder;
    chunksRef.current = [];

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    recorder.onstop = () => {
      // Release the microphone.
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setIsRecording(false);

      const blob = new Blob(chunksRef.current, {
        type: mimeTypeRef.current || "audio/webm",
      });
      chunksRef.current = [];

      void transcribe(blob);
    };

    recorder.start();
    setIsRecording(true);
  }, [isOnline, isRecording, isTranscribing, isSupported, transcribe]);

  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  }, []);

  return {
    isRecording,
    isTranscribing,
    isSupported,
    error,
    startRecording,
    stopRecording,
  };
}
